import { useEffect } from 'react';

/**
 * Per-route SEO tag manager for the client-rendered ObitAuthor SPA.
 *
 * Usage:
 *   useSeoTags({
 *     title: 'Page title — ObitAuthor',
 *     description: '…',
 *     canonical: 'https://obitauthor.com/path',
 *     ogImage: 'https://obitauthor.com/logo.png',
 *     jsonLd: [{ "@context": "https://schema.org", "@type": "…" }, ...]
 *   });
 *
 * The hook mutates the live <head> on mount and restores every tag it
 * touched on unmount, so consumer pages that don't declare SEO tags of
 * their own still see the baseline document meta.
 *
 * Why not react-helmet-async? Three reasons: (1) zero new dependencies
 * to keep `safe-build.sh` well under its memory cap, (2) we only need
 * a handful of tags and can manage them surgically, (3) we need
 * guaranteed cleanup between route transitions.
 */
export default function useSeoTags({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  robots = 'index, follow',
  jsonLd,
}) {
  // JSON.stringify used in deps so consumers can pass inline arrays
  // without a referential-equality trap.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const prevTitle = document.title;
    const tracked = []; // { el, prevValue | 'created' }

    const getOrCreateMeta = (attr, name) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (el) {
        tracked.push({ el, prev: el.getAttribute('content'), kind: 'meta-existing' });
        return el;
      }
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
      tracked.push({ el, kind: 'meta-created' });
      return el;
    };

    const setMetaByName = (name, content) => {
      if (content == null || content === '') return;
      const el = getOrCreateMeta('name', name);
      el.setAttribute('content', content);
    };
    const setMetaByProp = (prop, content) => {
      if (content == null || content === '') return;
      const el = getOrCreateMeta('property', prop);
      el.setAttribute('content', content);
    };
    const setLinkCanonical = (href) => {
      if (!href) return;
      let el = document.head.querySelector('link[rel="canonical"]');
      if (el) {
        tracked.push({ el, prev: el.getAttribute('href'), kind: 'link-existing' });
      } else {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
        tracked.push({ el, kind: 'link-created' });
      }
      el.setAttribute('href', href);
    };

    if (title) document.title = title;

    setMetaByName('description', description);
    setMetaByName('robots', robots);
    if (keywords) setMetaByName('keywords', keywords);

    // Open Graph — fall through to primary title/description if not overridden.
    const effectiveOgTitle = ogTitle || title;
    const effectiveOgDesc = ogDescription || description;
    setMetaByProp('og:title', effectiveOgTitle);
    setMetaByProp('og:description', effectiveOgDesc);
    setMetaByProp('og:type', ogType);
    if (canonical) setMetaByProp('og:url', canonical);
    if (ogImage) setMetaByProp('og:image', ogImage);

    // Twitter
    setMetaByName('twitter:card', twitterCard);
    setMetaByName('twitter:title', effectiveOgTitle);
    setMetaByName('twitter:description', effectiveOgDesc);
    if (ogImage) setMetaByName('twitter:image', ogImage);

    setLinkCanonical(canonical);

    // JSON-LD — append one <script> per blob, tag them with a data attr
    // so cleanup is unambiguous.
    const jsonLdEls = [];
    if (Array.isArray(jsonLd)) {
      for (const blob of jsonLd) {
        if (!blob) continue;
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.dataset.seoHook = 'route';
        try {
          s.textContent = JSON.stringify(blob);
        } catch (_) { continue; }
        document.head.appendChild(s);
        jsonLdEls.push(s);
      }
    }

    return () => {
      document.title = prevTitle;
      // Restore meta/link tags in reverse order so last-write-wins
      // behavior is preserved on fast consecutive route changes.
      for (let i = tracked.length - 1; i >= 0; i--) {
        const t = tracked[i];
        if (t.kind === 'meta-created' || t.kind === 'link-created') {
          t.el.parentNode && t.el.parentNode.removeChild(t.el);
        } else if (t.kind === 'meta-existing') {
          if (t.prev == null) t.el.removeAttribute('content');
          else t.el.setAttribute('content', t.prev);
        } else if (t.kind === 'link-existing') {
          if (t.prev == null) t.el.removeAttribute('href');
          else t.el.setAttribute('href', t.prev);
        }
      }
      for (const s of jsonLdEls) {
        s.parentNode && s.parentNode.removeChild(s);
      }
    };
  // Re-run the effect whenever any input changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, keywords, ogTitle, ogDescription, ogImage, ogType, twitterCard, robots, jsonLdKey]);
}
